import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Sparkles,
  RefreshCw,
  AlertCircle,
  Target,
} from "lucide-react";
import Cookies from "js-cookie";
import host from "../../../../../global";
import SubStepBreadcrumb from "./SubStepBreadcrumb";
import FocusAreaGrid from "./components/FocusAreaGrid";
import CustomFocusInput from "./components/CustomFocusInput";
import SkeletonCard from "./components/SkeletonCard";
import { calcMaxSelectableFocusAreas } from "./utils/jdUtils";

// Step 2b — Focus Area selection.
// Auto-fetches AI suggestions on mount; user can regenerate or add custom areas.
// Previously-selected cards are pinned after regen so they stay visible.

const CustomStep2b_FocusAreas = ({
  jobDescription,
  jobRole,
  roundType,
  currentFocus,
  onFocusChange,
  duration = 60,
  experienceLevel = "mid",
  disabled = false,
  focusAreasCache = null,
  onFocusAreasUpdate = null,
  onSubStepNav,
}) => {

  // ── State ──────────────────────────────────────────────────────────────────

  const [state, setState] = useState({
    focusAreas: focusAreasCache?.focusAreas || [],  // seeded from parent cache — avoids re-fetch on back-nav
    loading: false,
    selectedFoci: currentFocus
      ? currentFocus.split(",").map((f) => f.trim()).filter(Boolean)
      : [],
    customInput: "",
    error: null,
    initialized: true,
    generationAttempts: focusAreasCache?.generationAttempts || 0,
    previousGenerations: focusAreasCache?.previousGenerations || [],
    showCustomInput: false,
  });

  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);


  // ── Derived values ─────────────────────────────────────────────────────────

  const maxFocusAreas = useMemo(() => calcMaxSelectableFocusAreas(duration), [duration]);


  // ── Helpers ────────────────────────────────────────────────────────────────

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const showError = useCallback(
    (errorMessage, timeout = 2000) => {
      updateState({ error: errorMessage });
      setTimeout(() => updateState({ error: null }), timeout);
    },
    [updateState]
  );


  // ── Effects ────────────────────────────────────────────────────────────────

  // Reset selected focus areas when parent clears currentFocus (e.g. duration changed)
  useEffect(() => {
    if (currentFocus === "" && state.selectedFoci.length > 0) {
      updateState({ selectedFoci: [] });
    }
  }, [currentFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bubble selection changes up to parent (debounced 100ms)
  useEffect(() => {
    if (!state.initialized) return;

    const focusString = state.selectedFoci.join(", ");
    if (focusString === currentFocus) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFocusChange(focusString, state.selectedFoci);
    }, 100);
  }, [state.selectedFoci, state.initialized, currentFocus, onFocusChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);


  // ── AI generation ──────────────────────────────────────────────────────────

  const generateFocusAreas = useCallback(
    async (isRetry = false, regenerationType = "fresh") => {
      // Guard: job description required
      if (!jobDescription?.trim()) {
        updateState({
          error: "Job description is required for AI-powered focus area generation.",
          focusAreas: [],
        });
        return;
      }

      if (jobDescription.length < 50) {
        updateState({
          error: "Job description too brief. Please provide at least 50 characters with role requirements and responsibilities.",
          focusAreas: [],
        });
        return;
      }

      // Guard: max 3 attempts (reset on retry button press)
      if (!isRetry && state.generationAttempts >= 3) {
        updateState({
          error: "Maximum generation attempts reached. Please try again later or add custom focus areas.",
        });
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      updateState({
        loading: true,
        error: null,
        generationAttempts: state.generationAttempts + 1,
      });

      try {
        // Build dedup list: union of selected titles + last 3 generation batches
        const allPreviousTitles = [
          ...new Set([
            ...state.previousGenerations.flat(),
            ...state.selectedFoci,
          ]),
        ];

        const accessToken = Cookies.get("accessToken");
        const response = await fetch(`${host}/api/generate-focus-areas`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-Request-ID": `focus-${Date.now()}`,
            "X-Generation-Type": regenerationType,
          },
          body: JSON.stringify({
            job_description: jobDescription,
            round_type: roundType,
            experience_level: experienceLevel,
            duration: duration,
            job_role: jobRole || roundType,
            previous_focus_areas: allPreviousTitles,
            selected_focus_areas: state.selectedFoci,
            count: Math.max(1, 9 - state.selectedFoci.length),
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail || `Server error: ${response.status}. Please try again.`
          );
        }

        const data = await response.json();

        if (!data.success || !data.focus_areas?.length) {
          throw new Error(
            "AI couldn't generate meaningful focus areas from the job description. Please ensure it contains specific role requirements and technical details."
          );
        }

        const validAreas = data.focus_areas.filter(
          (area) => area.title && area.title.length > 3 && area.title.length <= 50
        );

        if (validAreas.length === 0) {
          throw new Error(
            "Generated focus areas don't meet quality standards. Please try again or add custom areas."
          );
        }

        const currentTitles = validAreas.map((area) => area.title);

        // Pin previously-selected cards that are missing from the new batch so
        // they remain visible and highlighted in the grid
        const missingSelected = state.selectedFoci
          .filter((title) => !currentTitles.includes(title))
          .map((title) => ({
            id: `selected-${title}`,
            title,
            description: "Previously selected focus area",
          }));

        // No slice — accumulate all shown titles permanently so the avoid list never shrinks
        const newPreviousGenerations = [...state.previousGenerations, currentTitles];
        const newFocusAreas = [...missingSelected, ...validAreas];

        updateState({
          focusAreas: newFocusAreas,
          loading: false,
          error: null,
          generationAttempts: 0,
          previousGenerations: newPreviousGenerations,
        });

        // Write back to parent so re-mount doesn't re-fetch
        onFocusAreasUpdate?.({
          focusAreas: newFocusAreas,
          generationAttempts: 0,
          previousGenerations: newPreviousGenerations,
        });

      } catch (error) {
        if (error.name === "AbortError") return;

        let errorMessage = "Focus area generation failed. ";

        if (error.name === "TimeoutError") {
          errorMessage += "Request timed out. The job description might be too complex. Please try again.";
        } else if (error.message.includes("rate limit") || error.message.includes("429")) {
          errorMessage += "Too many requests. Please wait 30 seconds and try again.";
        } else if (error.message.includes("network") || error.message.includes("fetch")) {
          errorMessage += "Network error. Please check your connection and try again.";
        } else if (error.message.length > 10) {
          errorMessage += error.message;
        } else {
          errorMessage += "Please try again or add focus areas manually using the Custom button.";
        }

        updateState({ focusAreas: [], loading: false, error: errorMessage });

      } finally {
        abortControllerRef.current = null;
      }
    },
    [
      jobDescription,
      roundType,
      experienceLevel,
      duration,
      maxFocusAreas,
      state.generationAttempts,
      state.previousGenerations,
      state.selectedFoci,
      updateState,
    ]
  );

  // Auto-fetch on mount (only if no areas yet and conditions are met)
  useEffect(() => {
    if (
      state.initialized &&
      roundType &&
      jobDescription &&
      jobDescription.length >= 50 &&
      state.focusAreas.length === 0 &&
      !state.loading &&
      state.generationAttempts === 0
    ) {
      generateFocusAreas(false, "initial");
    }
  }, [
    state.initialized,
    roundType,
    jobDescription,
    state.focusAreas.length,
    state.loading,
    state.generationAttempts,
    generateFocusAreas,
  ]);


  // ── Selection handlers ─────────────────────────────────────────────────────

  const toggleFocus = useCallback(
    (focus) => {
      const isSelected = state.selectedFoci.includes(focus);

      if (isSelected) {
        updateState({
          selectedFoci: state.selectedFoci.filter((f) => f !== focus),
          error: null,
        });
        return;
      }

      if (state.selectedFoci.length >= maxFocusAreas) {
        showError(
          `Maximum ${maxFocusAreas} focus areas recommended for optimal ${duration}-minute interview depth. Consider removing others first.`,
          4000
        );
        return;
      }

      updateState({
        selectedFoci: [...state.selectedFoci, focus],
        error: null,
      });
    },
    [state.selectedFoci, maxFocusAreas, duration, updateState, showError]
  );

  const addCustomFocus = useCallback(() => {
    const trimmedInput = state.customInput.trim();

    if (!trimmedInput) {
      showError("Please enter a focus area name.");
      return;
    }
    if (trimmedInput.length < 3) {
      showError("Focus area name must be at least 3 characters long.");
      return;
    }
    if (trimmedInput.length > 50) {
      showError("Focus area name must be less than 50 characters.");
      return;
    }
    if (state.selectedFoci.some((f) => f.toLowerCase() === trimmedInput.toLowerCase())) {
      showError("This focus area is already selected.");
      return;
    }
    if (state.selectedFoci.length >= maxFocusAreas) {
      showError(`Maximum ${maxFocusAreas} focus areas allowed for ${duration}-minute round.`, 3000);
      return;
    }

    // Also add to focusAreas so the card is visible/deselectable in the grid
    const customArea = {
      id: `custom-${trimmedInput}`,
      title: trimmedInput,
      description: "Custom focus area",
    };

    updateState({
      selectedFoci: [...state.selectedFoci, trimmedInput],
      focusAreas: [...state.focusAreas, customArea],
      customInput: "",
      error: null,
      showCustomInput: false,
    });
  }, [
    state.customInput,
    state.selectedFoci,
    maxFocusAreas,
    duration,
    updateState,
    showError,
  ]);


  // ── Render ─────────────────────────────────────────────────────────────────

  // Full skeleton — initial load only (no existing areas yet)
  if (!state.initialized || (state.loading && state.focusAreas.length === 0)) {
    return (
      <div className="space-y-6 mb-10">
        <SubStepBreadcrumb activeIndex={1} onStepClick={onSubStepNav} />
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span>AI is analyzing the job description...</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-10">
      <SubStepBreadcrumb activeIndex={1} onStepClick={onSubStepNav} />

      <div className="space-y-6">

        {/* Header: title + regenerate button */}
        <div className="flex items-center">
          <span className="text-xl dark:text-white text-textPrimary">
            Choose the main areas you want to focus on for this round
          </span>
          <span className="text-xl text-red-500">*</span>
          <button
            type="button"
            onClick={() => generateFocusAreas(true, "deeper")}
            disabled={state.loading || disabled}
            className="ml-4 flex items-center gap-1.5 px-3 py-2 text-xs rounded-full
              dark:bg-gray-700 bg-gray-100 dark:text-gray-300 text-textSecondary
              hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            <RefreshCw className={`w-3 h-3 ${state.loading ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        </div>

        {/* Selection counter */}
        <div className="flex items-center justify-between">
          <label className="dark:text-[#C9A3FF] text-textSecondary">
            {`Topics selected: ${state.selectedFoci.length}/${maxFocusAreas}`}
          </label>
        </div>

        {/* Error banner */}
        {state.error && (
          <div className="p-4 bg-red-900/20 border border-red-600 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-300 text-sm leading-relaxed">{state.error}</p>
                {state.generationAttempts > 1 && (
                  <div className="mt-2 text-xs text-red-400">
                    Attempt {state.generationAttempts}/3
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Focus area grid or empty state */}
        {state.focusAreas.length > 0 || jobDescription ? (
          <div className="space-y-3" style={{ contain: "layout style" }}>

            {/* Grid header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm dark:text-gray-400 text-textSecondary">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span>AI-generated focus areas for {roundType}</span>
              </div>
            </div>

            <FocusAreaGrid
              focusAreas={state.focusAreas}
              selectedFoci={state.selectedFoci}
              maxAllowed={maxFocusAreas}
              disabled={disabled}
              isRegenerating={state.loading}
              onToggle={toggleFocus}
            />

            <CustomFocusInput
              showCustomInput={state.showCustomInput}
              customInput={state.customInput}
              disabled={disabled}
              onInputChange={(e) => {
                const input = e.target.value;
                const isValidChars = /^[a-zA-Z0-9 ]*$/.test(input);
                const words = input.trim().split(/\s+/).filter(Boolean);
                const notOnlyNumbers = !/^\d+$/.test(input.trim());
                if (isValidChars && words.length <= 10 && notOnlyNumbers) {
                  updateState({ customInput: input });
                }
              }}
              onAdd={addCustomFocus}
              onCancel={() => updateState({ showCustomInput: false, customInput: "" })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomFocus();
                } else if (e.key === "Escape") {
                  updateState({ customInput: "", showCustomInput: false });
                }
              }}
              onToggleShow={() =>
                updateState({ showCustomInput: !state.showCustomInput, customInput: "" })
              }
            />
          </div>
        ) : (
          // Empty state — shown before first generation completes
          <div className="p-6 bg-gray-800/30 border border-gray-600 rounded-lg text-center">
            <Target className="w-10 h-10 mx-auto text-gray-400 opacity-50 mb-3" />
            <p className="text-gray-400 text-sm mb-2 font-medium">Ready for AI Analysis</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Provide a detailed job description to generate intelligent focus areas
              <br />
              tailored to the role requirements and interview objectives.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomStep2b_FocusAreas;
